/**
 * HeaderSlot — [T-DASHBOARD-VISIBLE-HEADER] v4 visible header row.
 *
 * The visible header row, replacing the headerless design of
 * [T-DASHBOARD-HEADERLESS]. The row lives ABOVE `BodySlot` in the widget
 * cell's flex column (NOT floated as an absolute overlay), so the header
 * consumes cell height (~36px) instead of overlapping the body data.
 *
 * ## Anatomy
 *
 *   - Outer `<div data-testid="widget-card-chrome" data-testid="widget-header">`:
 *     a 36px flex row. `border-b border-border/40` separates the header
 *     from the body. `shrink-0` so the header keeps its size when the
 *     widget cell shrinks.
 *   - Left side: optional `<span data-testid="widget-header-icon">` (icon)
 *     followed by a `<span data-testid="widget-header-title">` with the
 *     truncated title, OR an `<Input data-testid="widget-rename-input">`
 *     when `ctx.rename?.isRenaming === true`.
 *   - Right side: `<div data-testid="widget-header-actions">` containing
 *     `<WidgetHeaderActions />` (gear dropdown, edit-mode only).
 *   - A sr-only `<h3 id={headerId}>` is ALWAYS emitted for `aria-labelledby`
 *     regardless of whether the visible header is rendered (so screen
 *     readers always have a label target).
 *
 * ## Behaviour
 *
 * - If `useWidgetContext()` returns `null` (call site outside a provider):
 *   render only the sr-only h3 (backwards compat for Storybook stories +
 *   isolated unit tests). The visible row is omitted.
 * - If `ctx` is present but `showTitle === false`: render only the sr-only
 *   h3 (the visible row is suppressed but the aria target remains).
 * - Otherwise: render the full visible row.
 *
 * ## Rename input
 *
 * When `ctx.rename?.isRenaming === true`, the title span is swapped for
 * an `<Input data-testid="widget-rename-input">`. Enter commits, Escape
 * cancels, blur commits — matches the v3.2 rename interaction in
 * `WidgetCardChrome.tsx`.
 *
 * ## Testids
 *
 * Preserved (compat with `WidgetCardChrome`):
 *   - `widget-card-chrome`  (the row container, alias of `widget-header`)
 *   - `widget-rename-input` (the rename `<Input>`)
 *
 * New semantic:
 *   - `widget-header`        (alias for `widget-card-chrome`)
 *   - `widget-header-icon`   (icon wrapper, when icon present)
 *   - `widget-header-title`  (title span)
 *   - `widget-header-actions` (right-aligned actions wrapper)
 */
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { HeaderState } from './types';
import { useWidgetContext } from '../WidgetContext';
import { WidgetHeaderActions } from '../WidgetHeaderActions';

export interface HeaderSlotProps extends HeaderState {
  /** Passed through to allow parent to drive pendingRemove confirmations. */
  pendingRemove?: boolean;
}

/**
 * Header row component.
 *
 * Reads mode + actions + rename state from `WidgetContext`. Falls back to
 * sr-only h3 when no provider is mounted (Storybook / unit-test safety).
 */
export function HeaderSlot({
  headerId,
  title,
  icon,
  showTitle = true,
  pendingRemove: _pendingRemove,
}: HeaderSlotProps): ReactNode {
  const ctx = useWidgetContext();

  // Safety net 1: no provider mounted → render sr-only h3 only.
  // This keeps `HeaderSlot` usable in Storybook stories + isolated unit
  // tests that render it directly without a full dashboard provider.
  // Safety net 2: provider mounted but `showTitle === false` → also sr-only.
  if (!ctx || !showTitle) {
    return (
      <h3 id={headerId} className="sr-only">
        {title}
      </h3>
    );
  }

  // Rename input is the edit-mode title swap. Preview mode has no rename.
  const rename = ctx.rename;
  const isRenaming = rename?.isRenaming === true;

  return (
    <>
      {/* Always emit the sr-only h3 for the aria-labelledby chain. */}
      <h3 id={headerId} className="sr-only">
        {title}
      </h3>

      {/* Visible header row (36px). */}
      <div
        data-testid="widget-card-chrome"
        data-widget-header-mode={ctx.mode}
        data-widget-id={ctx.widgetId}
        className={cn(
          'flex items-center justify-between gap-2 px-2 py-1.5 border-b border-border/40 shrink-0',
        )}
      >
        {/* Left side: icon + title (or rename input). */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {icon ? (
            <span
              data-testid="widget-header-icon"
              className="shrink-0"
              aria-hidden="true"
            >
              {icon}
            </span>
          ) : null}

          {isRenaming && rename ? (
            <Input
              data-testid="widget-rename-input"
              value={rename.value}
              onChange={(event) => rename.onChange(event.target.value)}
              onBlur={() => rename.onCommit()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') rename.onCommit();
                if (event.key === 'Escape') rename.onCancel();
              }}
              autoFocus
              className="h-6 text-xs"
            />
          ) : (
            <span
              data-testid="widget-header-title"
              className="text-xs font-medium truncate"
              onDoubleClick={
                ctx.mode === 'edit' && rename
                  ? () => rename.onStartRename()
                  : undefined
              }
            >
              {title}
            </span>
          )}
        </div>

        {/* Right side: gear dropdown in edit mode. */}
        <div
          data-testid="widget-header-actions"
          className="flex items-center shrink-0"
        >
          <WidgetHeaderActions />
        </div>
      </div>
    </>
  );
}
